import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import ReactDataSheet from 'react-datasheet';
import {
  Box, Button, ButtonVariant, Chart, IconName, Picker, Text, Tooltip,
} from '@usealloy/component-library';
import AxisHeaderInput from 'flow/editor/components/NodeEditorDrawer/MatrixModel/AxisHeaderInput';
import type { OnAxisLengthChangeProps, OnAxisValueChangeProps } from 'flow/editor/components/NodeEditorDrawer/MatrixModel/AxisHeaderInput/types';
import type { GridElement } from 'flow/editor/components/NodeEditorDrawer/MatrixModel/MatrixModel';
import { RowHeaderDropdownMenu } from 'flow/editor/components/NodeEditorDrawer/MatrixModel/RowHeaderDropdownMenu';
import {
  CellDiv,
  CellLabel,
  StyledMatrixCell,
  StyledMatrixRowHeader,
  StyledMatrixRowMenuLabel,
} from 'flow/editor/components/NodeEditorDrawer/MatrixModel/styles';
import { useEditorAccess } from 'flow/editor/context';
import {
  AXIS_HIGH_UNBOUNDED_NUMBER,
  AXIS_LOW_UNBOUNDED_NUMBER,
  getCellLabel,
  HighRangeOption,
  LowRangeOption,
  MatrixAxisOperation,
} from 'flow/service/actions';
import { getIsEditable } from 'flow/service/general';
import type { OutputValueRange, ValueRangeConfigOptions } from 'flow/store/engine/types';
import {
  calculateColorRanges,
  createNewValueRangeConfig,
  deleteOneValueRange,
  getIsDeleteDisabled,
  getUpdatedAxis,
  insertOneValueRange,
} from './helpers';
import { StyledTable } from './styles';
import { ValueRangeConfigPopover } from './ValueRangeConfigPopover';

interface ValueRangeConfigTableProps {
  outputValueRange: OutputValueRange[];
  outputValueRangeOptions: ValueRangeConfigOptions;
  updateOutputValueRange: (outputValueRange: OutputValueRange[]) => void;
  updateOutputValueRangeOptions: (outputValueRangeOptions: ValueRangeConfigOptions) => void;
}

export const ValueRangeConfigTable = ({
  outputValueRange,
  outputValueRangeOptions,
  updateOutputValueRange,
  updateOutputValueRangeOptions,
}: ValueRangeConfigTableProps) => {
  const [highlightedRow, setHighlightedRow] = useState<number | null>(null);
  const { editorMode } = useEditorAccess();
  const inEditMode = getIsEditable(editorMode);
  const convertedAxisHeader = useMemo(
    () => {
      const axisHeader = [...outputValueRange.map(({ minValue }) => minValue)];
      if (outputValueRangeOptions?.lowRange === LowRangeOption.unbounded) {
        axisHeader[0] = AXIS_LOW_UNBOUNDED_NUMBER;
      }
      if (outputValueRangeOptions?.highRange === HighRangeOption.unbounded) {
        axisHeader.push(AXIS_HIGH_UNBOUNDED_NUMBER);
      } else if (
        outputValueRangeOptions?.highRange === HighRangeOption.bounded
        && outputValueRange.length > 0
      ) {
        axisHeader.push(outputValueRange[outputValueRange.length - 1].maxValue);
      }
      return axisHeader;
    },
    [outputValueRangeOptions, outputValueRange],
  );

  const initializeEmptyConfig = useCallback(() => {
    const newValueRangeConfig = [createNewValueRangeConfig({ id: '1' })];
    updateOutputValueRangeOptions({
      lowRange: LowRangeOption.bounded,
      highRange: HighRangeOption.bounded,
    });
    updateOutputValueRange(newValueRangeConfig);
  }, [updateOutputValueRange, updateOutputValueRangeOptions]);

  useEffect(() => {
    if (!outputValueRange?.length) {
      initializeEmptyConfig();
    }
  }, [initializeEmptyConfig, outputValueRange.length]);

  const onAxisValueChange = useCallback(({
    index, value,
  }: OnAxisValueChangeProps) => {
    const updatedValue = {
      ...outputValueRange[index],
      minValue: value as number,
    };
    const updatedPreviousValue = {
      ...outputValueRange[index - 1],
      maxValue: value as number,
    };

    const updatedAxis = getUpdatedAxis({
      index,
      outputValueRange,
      updatedPreviousValue,
      updatedValue,
    });
    updateOutputValueRange(updatedAxis);
  }, [outputValueRange, updateOutputValueRange]);

  const onAxisLengthChange = useCallback(({
    op,
  }: OnAxisLengthChangeProps) => {
    const updatedOutputValueRange = JSON.parse(JSON.stringify(outputValueRange));
    if (op === MatrixAxisOperation.DELETE) {
      updatedOutputValueRange.pop();
      if (outputValueRangeOptions.highRange === HighRangeOption.unbounded) {
        updatedOutputValueRange[updatedOutputValueRange.length - 1].maxValue = null;
      }
    } else if (op === MatrixAxisOperation.ADD) {
      updatedOutputValueRange.push(createNewValueRangeConfig({
        id: `${Number(outputValueRange[outputValueRange.length - 1].id) + 1}`,
        min: outputValueRange[outputValueRange.length - 1].maxValue,
      }));
    }
    updateOutputValueRange(updatedOutputValueRange);
  }, [outputValueRange, outputValueRangeOptions?.highRange, updateOutputValueRange]);

  const getAxisHeaders = useCallback(() => {
    const convertedAxisSettings = {
      axisHeader: convertedAxisHeader,
      axisHeaderDataType: 'number',
      order: 'asc',
    };
    const axisValuesToMap = convertedAxisHeader.length === 1
      ? convertedAxisHeader
      // each range is handled with index and index + 1
      : convertedAxisHeader.slice(0, -1);

    const axisHeaders: any[] = [];

    axisValuesToMap.forEach((value: number, index: number) => {
      axisHeaders.push({
        value,
        valueViewer: () => (
          <span
            data-dd-action-name={`value-range-config-axis-header-y-${index + 1}`}
          >
            <AxisHeaderInput
              axis="Y"
              axisSettings={convertedAxisSettings}
              index={index}
              onChange={onAxisValueChange}
              onDelete={() => onAxisLengthChange({
                axis: 'Y', op: MatrixAxisOperation.DELETE,
              })}
            />
          </span>
        ),
        disableEvents: true,
      });
    });
    return axisHeaders;
  }, [convertedAxisHeader, onAxisLengthChange, onAxisValueChange]);

  const onLabelChange = useCallback((
    changedLabelCells: ReactDataSheet.CellsChangedArgs<GridElement, string>,
  ) => {
    // early exit if onLabelChange is called with no changedLabelCells
    if (!changedLabelCells?.length) return;

    const [{ value, row }] = changedLabelCells;
    const index = row - 1;
    const updatedOutputValueRange = [
      ...outputValueRange.toSpliced(
        index,
        1,
        {
          ...outputValueRange[index],
          label: value,
        },
      ),
    ];
    updateOutputValueRange(updatedOutputValueRange);
  }, [outputValueRange, updateOutputValueRange]);

  const onColorChange = useCallback(({ color, index }: { color: string; index: number }) => {
    const updatedOutputValueRange = [
      ...outputValueRange.toSpliced(
        index,
        1,
        {
          ...outputValueRange[index],
          color,
        },
      ),
    ];
    updateOutputValueRange(updatedOutputValueRange);
  }, [outputValueRange, updateOutputValueRange]);

  const matrixGridToRender = useMemo(() => {
    const headerCol = getAxisHeaders();

    const matrixGrid = [[]];
    return headerCol.reduce((acc, curr, index) => {
      const arr = [];
      const cellLabelValue = getCellLabel(0, index);
      const label = outputValueRange[index]?.label;
      const color = outputValueRange[index]?.color;
      // add the header cell
      arr.push(curr);
      // add the value cell
      arr.push({
        value: label,
        readOnly: false,
        valueViewer: ({ value: cellValue }) => (
          <Box data-dd-action-name={`value-range-config-cell-${label}`}>
            <CellLabel>{cellLabelValue}</CellLabel>
            <CellDiv>{cellValue}</CellDiv>
          </Box>
        ),
      });
      // add the color picker cell
      arr.push({
        value: label,
        readOnly: true,
        valueViewer: () => (
          <Box data-dd-action-name={`value-range-config-cell-${label}`}>
            <Picker
              style={{ border: 'none' }}
              variant="color"
              value={color}
              onChange={newColor => {
                onColorChange({ color: newColor, index });
              }}
            />
          </Box>
        ),
      });
      acc.push(arr);
      return acc;
    }, matrixGrid);
  }, [getAxisHeaders, onColorChange, outputValueRange]);

  const { colorRanges, animatedRangeIndices, hasAtLeastOneColor } = useMemo(
    () => calculateColorRanges({
      outputValueRange,
      outputValueRangeOptions,
      includePendingRanges: true,
    }),
    [outputValueRange, outputValueRangeOptions],
  );

  const onInsertRow = useCallback(({ row, insertDirection }: { row: number; insertDirection: 'before' | 'after' }) => {
    const updatedValueRanges = insertOneValueRange({ row, outputValueRange, insertDirection });
    updateOutputValueRange(updatedValueRanges);
  }, [outputValueRange, updateOutputValueRange]);

  const onDeleteRow = useCallback((row: number) => {
    const updatedValueRanges = deleteOneValueRange({
      row, outputValueRange, outputValueRangeOptions,
    });
    updateOutputValueRange(updatedValueRanges);
  }, [outputValueRange, outputValueRangeOptions, updateOutputValueRange]);

  const disableDeleteRow = useMemo(() => (
    getIsDeleteDisabled({ outputValueRange, outputValueRangeOptions })
  ), [outputValueRange, outputValueRangeOptions]);

  return (
    <Box
      direction="column"
      gap={0}
      justifyContent="flex-start"
      style={{ marginLeft: 'calc(-1 * var(--cl-space-3)' }}
    >
      <Box justifyContent="flex-start" style={{ paddingLeft: 'var(--cl-space-3)' }}>
        <Text>Configure Value Ranges</Text>
        <ValueRangeConfigPopover
          outputValueRange={outputValueRange}
          outputValueRangeOptions={outputValueRangeOptions}
          updateOutputValueRange={updateOutputValueRange}
          updateOutputValueRangeOptions={updateOutputValueRangeOptions}
        />
      </Box>
      {outputValueRange.length > 0 && hasAtLeastOneColor && (
        <div
          style={{
            minWidth: '100%',
            marginTop: 'calc(-1 * var(--cl-space-7))',
            zIndex: -1,
          }}
        >
          <Chart.Bullet
            colorRanges={colorRanges}
            animatedRangeIndices={animatedRangeIndices}
          />
        </div>
      )}
      <Box direction="column" alignItems="flex-start" justifyContent="flex-start">
        <StyledTable style={{ padding: '0 var(--cl-space-3)', width: '100%' }}>
          <ReactDataSheet
            data={matrixGridToRender}
            cellRenderer={({
              children, row, col, ...rest
            }) => (col === 0 && row !== 0 ? (
              <StyledMatrixRowHeader {...rest}>
                <div style={{ height: '100%', borderRadius: 'inherit' }}>
                  <RowHeaderDropdownMenu
                    disabled={!inEditMode}
                    disableDeleteRow={disableDeleteRow}
                    isOpen={highlightedRow === row + 1}
                    onDeleteRow={() => { onDeleteRow(row - 1); }}
                    onInsertAfter={() => { onInsertRow({ row, insertDirection: 'after' }); }}
                    onInsertBefore={() => { onInsertRow({ row: row - 1, insertDirection: 'before' }); }}
                    onOpenChange={isOpen => { if (!isOpen) { setHighlightedRow(null); } }}
                  />
                  <Tooltip content="Click to view menu" style={{ fontSize: 'var(--cl-font-size-1)', padding: 'var(--cl-space-1) var(--cl-space-2)' }}>
                    <StyledMatrixRowMenuLabel
                      disabled={!inEditMode}
                      highlighted={highlightedRow === row + 1}
                      onClick={isOpen => { setHighlightedRow(isOpen ? row + 1 : null); }}
                    >
                      {row}
                    </StyledMatrixRowMenuLabel>
                  </Tooltip>
                </div>
                {children}
              </StyledMatrixRowHeader>
            ) : (
              <StyledMatrixCell {...rest}>
                {children}
              </StyledMatrixCell>
            )
            )}
            onCellsChanged={onLabelChange}
            valueRenderer={(cell: GridElement) => cell.value}
          />
        </StyledTable>
        <Tooltip content="Add Row">
          <Box
            justifyContent="flex-start"
            width="auto"
            data-dd-action-name="value-range-config-add-new-row"
          >
            <Button
              icon={IconName.Add}
              type="button"
              variant={ButtonVariant.tertiary}
              onClick={() => onAxisLengthChange({
                axis: 'Y', op: MatrixAxisOperation.ADD,
              })}
            />
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};
